// NOTICE: KAPTION-INTEGRATION
// smart-availability.UI.1 — the availability rows for the inbox the open conversation is
// in, as the account's endpoint answered them, kept per inbox.
//
// Not merged into `inboxAssignableAgents`: that list is fetched when a conversation opens and
// cached; a queue changes minute to minute. This module is refreshed by the pickers while
// they are open and read by everything that renders a person or a team as a target.
import KaptionSmartAvailabilityAPI from '../../api/kaptionSmartAvailability';

const state = {
  // inboxId → { agents: {agentId → info}, teams: {teamId → info}, limit, stale, showCounts, fetchedAt }
  records: {},
  requests: {},
  uiFlags: { isFetching: false },
};

export const types = {
  START_REQUEST: 'START_REQUEST',
  SET_KAPTION_SMART_AVAILABILITY: 'SET_KAPTION_SMART_AVAILABILITY',
  SET_KAPTION_SMART_AVAILABILITY_UI_FLAG:
    'SET_KAPTION_SMART_AVAILABILITY_UI_FLAG',
  CLEAR_KAPTION_SMART_AVAILABILITY: 'CLEAR_KAPTION_SMART_AVAILABILITY',
};

const indexBy = rows =>
  (Array.isArray(rows) ? rows : []).reduce((acc, row) => {
    if (row && Number.isInteger(row.id)) acc[row.id] = row;
    return acc;
  }, {});

export const getters = {
  // smart-availability.UI.2 — undefined means UNKNOWN, never "unavailable". A picker that
  // gets undefined draws today's presence dot.
  forAgent: $state => (inboxId, agentId) =>
    $state.records[inboxId]?.agents?.[agentId],
  forTeam: $state => (inboxId, teamId) =>
    $state.records[inboxId]?.teams?.[teamId],
  hasData: ($state, _getters, _rootState, rootGetters) => inboxId => {
    const record = $state.records[inboxId];
    return (
      !!record &&
      !record.stale &&
      record.conversationId === rootGetters.getSelectedChat?.id
    );
  },
  limit: $state => inboxId => $state.records[inboxId]?.limit ?? null,
  isStale: $state => inboxId => !!$state.records[inboxId]?.stale,
  showCounts: $state => inboxId => !!$state.records[inboxId]?.showCounts,
  getUIFlags: $state => $state.uiFlags,
};

export const actions = {
  // smart-availability.API.3 — a 404 (flag off, endpoint unconfigured) and a network error
  // both end as "no data for this inbox": the picker shows what it always showed.
  async fetch({ commit, state: $state }, { inboxId, conversationId } = {}) {
    if (!inboxId) return;
    const requestId = ($state.requests[inboxId] || 0) + 1;
    commit(types.START_REQUEST, { inboxId, requestId });
    commit(types.SET_KAPTION_SMART_AVAILABILITY_UI_FLAG, { isFetching: true });
    try {
      const { data } = await KaptionSmartAvailabilityAPI.fetch({
        inboxId,
        conversationId,
      });
      if ($state.requests[inboxId] !== requestId) return;
      commit(types.SET_KAPTION_SMART_AVAILABILITY, {
        inboxId,
        conversationId,
        data,
      });
    } catch (error) {
      if ($state.requests[inboxId] === requestId) {
        commit(types.CLEAR_KAPTION_SMART_AVAILABILITY, { inboxId });
      }
    } finally {
      commit(types.SET_KAPTION_SMART_AVAILABILITY_UI_FLAG, {
        isFetching: false,
      });
    }
  },
};

export const mutations = {
  [types.START_REQUEST]($state, { inboxId, requestId }) {
    $state.requests = { ...$state.requests, [inboxId]: requestId };
  },
  [types.SET_KAPTION_SMART_AVAILABILITY](
    $state,
    { inboxId, conversationId, data }
  ) {
    $state.records = {
      ...$state.records,
      [inboxId]: {
        conversationId,
        agents: indexBy(data?.agents),
        teams: indexBy(data?.teams),
        limit: Number.isInteger(data?.limit) ? data.limit : null,
        stale: !!data?.stale,
        showCounts: !!data?.show_counts,
        fetchedAt: Date.now(),
      },
    };
  },
  [types.CLEAR_KAPTION_SMART_AVAILABILITY]($state, { inboxId }) {
    const records = { ...$state.records };
    delete records[inboxId];
    $state.records = records;
  },
  [types.SET_KAPTION_SMART_AVAILABILITY_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
